import React from 'react';
import { Card } from 'react-bootstrap';
import { BloodworkAnalysis, FurtherConsiderations, Section } from '../../models';
import {
  HOLOBIOME_ANCHOR,
  HOLOBIOME_KITS,
  HolobiomeKit,
  SYSTEMIC_FORMULAS_BRAND,
  normalizeSupplementName,
} from '../../models/holobiomeKits';
import {
  ClientListSelection,
  HolobiomeProductTable,
  HolobiomeTopPriorityTable,
  ListingChoice,
  PRODUCT_TABLE_LEAD,
  ProductTableIntro,
  SECTION_HEADINGS,
  ScheduledAddOn,
  SegmentedToggle,
  TopPriority,
  TopPriorityIntro,
  TopPriorityTable,
  UnrepresentedNote,
  topPriorityLead,
  topPriorityLegend,
  unrepresentedSystemCount,
  collectScheduledAddOns,
  collectTopPriorities,
  memoized,
  scopedGroups,
} from './supplements-summary-panel';
import { sectionShortLabel } from './results-overview-panel';
import { systemicFormulasProductUrl } from '../../utils/supplementItem';
import { packHolobiomeItems } from '../../utils/pdfHolobiomePagination';

// Canonical section order, taken from the Supplements panel's ordering so the
// "from:" links read top-to-bottom the way the report is laid out.
const SECTION_ORDER: Section[] = SECTION_HEADINGS.map(h => h.section);

// sectionShortLabel is the Results Overview's own map of compact system names
// (see results-overview-panel.tsx). This panel used to keep a near-copy, which
// had already drifted — it said "Iron" where the overview box says "Iron
// Status". One map now, so a "from:" link always reads the way the box the user
// lands on does.

// Fallback Systemic Formulas product page for a Holobiome product whose real
// URL isn't in the supplement catalog yet. The preferred source is the catalog
// link (furtherConsiderations.supplementLinkByName), which carries the sheet's
// exact URL; this slug guess only runs when a product hasn't been wired into the
// catalog. The micro sign is URL-encoded to %c2%b5 (not folded to "u") to match
// the site's own scheme — e.g. "Terra µBiomic" → terra-%c2%b5biomic.
const sfProductUrlFallback = systemicFormulasProductUrl;

// Resolve a product's "learn more" URL: the catalog link from the Supplements
// tab of the sheet first, the slug guess only as a last resort.
const productUrl = (
  name: string,
  furtherConsiderations: FurtherConsiderations,
): string =>
  furtherConsiderations.supplementLinkByName(name) ?? sfProductUrlFallback(name);

// The Holobiome Roadmap is a Systemic Formulas protocol, so the whole section —
// kits AND the product table under them — is scoped to that brand. Another
// brand's products (e.g. BWH Labs' Beta Glucan) are recommendations, but
// they're not part of this roadmap, so they belong to the Supplements panel
// only.

// True when the practitioner's Supplement Settings have Systemic Formulas
// checked. An undefined list means no brand filter is in play at all (the
// callers that don't pass one), which leaves the brand enabled.
export function isSystemicFormulasEnabled(brands: string[] | undefined): boolean {
  return !brands || brands.includes(SYSTEMIC_FORMULAS_BRAND);
}

// A product some identified pattern recommends, with everything the panel needs
// to render it: the display name (as resolveSupplement returns it — the master
// supplement title, spelled the same way HOLOBIOME_KITS spells its products),
// the catalog description/link, the section(s) it came from, and the brand(s)
// whose rows recommended it.
export interface RecommendedProduct {
  name: string;
  description: string;
  // The sheet's own URL for the product, when it carries one. A kit product can
  // fall back to a Systemic Formulas slug guess; an add-on only links when the
  // sheet carries a real URL.
  link?: string;
  sections: Section[];
  brands: string[];
}

// Map each recommended supplement (by normalized display name) to the product
// and the sections whose identified patterns recommend it. Same traversal the
// Supplements panel uses (possibleRootCauseCategoriesFor → Supplements
// considerations → resolveSupplement); a product recommended through either its
// primary or its "-2" (section-specific) tag resolves to the same name, and a
// product can be recommended by patterns in more than one section (e.g. EnerGenic
// from both Immune and Glucose), which is exactly why each origin is listed.
// Memoized on the same terms scopedGroups is (see `memoized` in
// supplements-summary-panel): this walks every section's considerations too,
// the protocols panel asks for it on every render, and the answer only changes
// when the analysis or the considerations are replaced. Ticking a Client List
// box re-rendered the panel and re-ran this walk before React could paint.
//
// The Map is SHARED, not copied. Callers read it; one that started to mutate it
// would be changing what the rest of the page sees.
const RECOMMENDED_CACHE = new WeakMap<
  object,
  WeakMap<object, Map<string, Map<string, RecommendedProduct>>>
>();

export function collectRecommendedProducts(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Map<string, RecommendedProduct> {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return new Map();
  return memoized(
    RECOMMENDED_CACHE,
    bloodworkAnalysis,
    furtherConsiderations,
    (brands ?? ['*']).join(','),
    () =>
      computeRecommendedProducts(
        bloodworkAnalysis,
        furtherConsiderations,
        brands,
      ),
  );
}

function computeRecommendedProducts(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Map<string, RecommendedProduct> {
  const collected = new Map<
    string,
    {
      name: string;
      description: string;
      link?: string;
      sections: Set<Section>;
      brands: Set<string>;
    }
  >();
  SECTION_HEADINGS.forEach(({ section }) => {
    const categories =
      bloodworkAnalysis.possibleRootCauseCategoriesFor?.(section) ?? [];
    categories.forEach(category => {
      const matches = furtherConsiderations.findConsiderations(
        category.rootCauseCategory,
        section,
        'Supplements',
        brands,
      );
      matches.forEach(consideration => {
        (consideration.items || []).forEach(item => {
          const parsed = furtherConsiderations.resolveSupplement(item);
          if (!parsed?.name) return;
          const key = normalizeSupplementName(parsed.name);
          if (!collected.has(key)) {
            collected.set(key, {
              name: parsed.name,
              description: parsed.description || '',
              link: parsed.link,
              sections: new Set(),
              brands: new Set(),
            });
          }
          collected.get(key)!.sections.add(section);
          if (consideration.brand) {
            collected.get(key)!.brands.add(consideration.brand);
          }
        });
      });
    });
  });
  const ordered = new Map<string, RecommendedProduct>();
  collected.forEach((entry, key) =>
    ordered.set(key, {
      name: entry.name,
      description: entry.description,
      link: entry.link,
      sections: SECTION_ORDER.filter(s => entry.sections.has(s)),
      brands: Array.from(entry.brands),
    }),
  );
  return ordered;
}

// Thin wrapper: normalized name → the section(s) that recommend it.
export function collectRecommendedSupplementSections(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Map<string, Section[]> {
  const sections = new Map<string, Section[]>();
  collectRecommendedProducts(
    bloodworkAnalysis,
    furtherConsiderations,
    brands,
  ).forEach((product, key) => sections.set(key, product.sections));
  return sections;
}

// Thin wrapper: just the set of recommended supplement names (used by tests).
export function collectRecommendedSupplementNames(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Set<string> {
  return new Set(
    collectRecommendedSupplementSections(
      bloodworkAnalysis,
      furtherConsiderations,
      brands,
    ).keys(),
  );
}

// Non-kit recommendations used to be collected here, unordered and unscheduled,
// for a standalone "Add-On Products" table. They now come from
// collectScheduledAddOns in the Supplements panel instead, because the roadmap
// parks each one under the month it starts and only that collection knows the
// month — assignAddOnMonths places products by severity with a per-month cap and
// spill-forward, so the schedule depends on the whole test rather than on a
// product's identity. Sharing one collection is also what stops the month on the
// rail disagreeing with the month in the by-system table.

// Whether the report should carry a Holobiome Roadmap section at all.
//
// Purely the Supplement Settings checkbox: the roadmap IS the Systemic Formulas
// protocol, so checking or unchecking that brand shows or hides the section
// whole — kits, product table and all. It deliberately does NOT depend on this
// test's recommendations. The five kits are the protocol itself, and a
// practitioner who has the brand switched on wants to see it whether or not a
// pattern happened to land inside a kit this time; a test with no Systemic
// Formulas recommendations just shows the roadmap with nothing highlighted.
export function hasSupplementProtocolsContent(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): boolean {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return false;
  return isSystemicFormulasEnabled(brands);
}

interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
  // Jump to a report section. When omitted, the "from:" section origins render
  // as plain text instead of links (e.g. in the PDF).
  onSectionClick?: (section: Section) => void;
  // The client-list checkbox column on the by-system cards. Omitted where the
  // practitioner can't edit, which is what leaves the column out rather than
  // rendering it disabled.
  clientList?: ClientListSelection;
}

// Mix a 6-digit hex toward white — 0 keeps the color, 1 is white. Lets each kit
// derive its pale row tint and muted accent from its single color, the way the
// Results Overview derives STATUS_TINT from its status palette. Solid 6-digit
// output (no rgba) so it rasterizes cleanly through html2canvas in the PDF.
const mixWithWhite = (hex: string, amount: number): string => {
  const value = parseInt(hex.replace('#', ''), 16);
  const channel = (shift: number): string => {
    const c = (value >> shift) & 0xff;
    return Math.round(c + (255 - c) * amount)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(16)}${channel(8)}${channel(0)}`;
};

// TWO row states.
//
// `identified` — a pattern in this test points at this product. Deeper tint,
// full-strength accent, bold, a tick, and its recommending section(s) beneath.
//
// `inKit` — ships in the kit, nothing pointed at it this time. A normal,
// present row: EVERY product in a kit is bought, so a neutral tint is right and
// graying it out would read as "not needed".
//
// There was a third, `unread`, for the products no pattern can reach — a dashed
// outline and a "not read by blood work" label under the name. It is gone: the
// products it named are waiting on pattern authoring, not on a marker that
// doesn't exist, and it only ever named two of the eight in that position. A
// row that says nothing about coverage is the honest rendering of a gap in the
// content — see holobiomeKits.ts.
export type RowState = 'identified' | 'inKit';

// The name stays dark in EVERY state. It used to take the kit color when
// recommended, which put the card's lowest-contrast text on the one thing people
// actually read. The tint and the accent carry the kit's identity; the name
// carries the words.
const productRowStyle = (
  kit: HolobiomeKit,
  state: RowState,
): React.CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  // 4px, not 2: at 11px the origins line sat close enough to the name that a
  // two-section row ("Immune · Glucose") read as part of the product name.
  gap: '4px',
  padding: '6px 9px',
  borderRadius: '6px',
  border: 'none',
  // 0.82 rather than the old 0.87: against the #f7f8f9 of an unhighlighted row
  // the paler tint was close to invisible, and it is the primary signal.
  borderLeft: `4px solid ${
    state === 'identified' ? kit.color : mixWithWhite(kit.color, 0.62)
  }`,
  backgroundColor:
    state === 'identified' ? mixWithWhite(kit.color, 0.82) : '#f7f8f9',
  color: '#212529',
  fontSize: '12px',
});

// The section(s) that recommended a product, each a jump link when
// onSectionClick is provided. A product can be recommended by more than one
// section, so all are listed. Used as line 2 inside an active kit box (white on
// the kit color) and in the Add-On Products table (blue on white).
function SectionOrigins({
  sections,
  onSectionClick,
  color = '#fff',
  style,
}: {
  sections: Section[];
  onSectionClick?: (section: Section) => void;
  color?: string;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <div
      style={{
        fontSize: '10.5px',
        letterSpacing: 'normal',
        lineHeight: 1.2,
        opacity: 0.95,
        ...style,
      }}
    >
      {sections.map((section, i) => (
        <React.Fragment key={section}>
          {i > 0 && <span> · </span>}
          {onSectionClick ? (
            <span
              role="button"
              tabIndex={0}
              onClick={() => onSectionClick(section)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSectionClick(section);
                }
              }}
              style={{ color, cursor: 'pointer', textDecoration: 'underline' }}
            >
              {sectionShortLabel(section)}
            </span>
          ) : (
            <span>{sectionShortLabel(section)}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// Split a kit's products into the two row states, identified FIRST.
//
// Sorting matters more than it sounds: in declared order the highlighted rows
// scatter through the card and the reader has to scan all six to find them.
// Leading with them means every card opens on what this test actually points
// to, and the remainder reads as included value rather than filler — which
// helps the kit sale rather than undercutting it.
export function classifyKitProducts(
  kit: HolobiomeKit,
  sectionsByName: Map<string, Section[]>,
): { identified: string[]; inKit: string[]; total: number } {
  const identified: string[] = [];
  const inKit: string[] = [];
  kit.productNames.forEach(name => {
    const sections = sectionsByName.get(normalizeSupplementName(name));
    if (sections && sections.length > 0) identified.push(name);
    else inKit.push(name);
  });
  return { identified, inKit, total: kit.productNames.length };
}

// One kit card — the Results Overview group card shape: white, a 4px top accent
// in the kit's step on the ramp, a header under a kit-colored title, then a
// column of section-style rows led by the identified ones.
//
// The header carries a COVERAGE METER: "3 of 5 products identified". The
// denominator is the whole kit — every product in it is bought, so every
// product in it is what the fraction is out of.
//
// A kit nothing points to gets a sentence naming the phase it still runs,
// rather than an empty card. An empty card is not neutral: the reader reads it
// as "skip this month", which is both wrong and the single most expensive thing
// this panel could accidentally say.
//
// `cardWidth` is a percentage on the rail so five columns share the row; the
// PDF passes its own (see PDF_KIT_CARD_WIDTH), which is why this takes a CSS
// length as well as a number. `boxShadow` is overridable for the same reason.
function KitCard({
  kit,
  sectionsByName,
  furtherConsiderations,
  onSectionClick,
  cardWidth = '100%',
  boxShadow = '0 1px 3px rgba(0, 0, 0, 0.06)',
  showMonth = false,
}: {
  kit: HolobiomeKit;
  sectionsByName: Map<string, Section[]>;
  furtherConsiderations: FurtherConsiderations;
  onSectionClick?: (section: Section) => void;
  cardWidth?: number | string;
  boxShadow?: string;
  // On the rail the month comes from the node above the card, so the title is
  // just the kit name. The PDF has no rail, so it puts the month back on the
  // title — otherwise the printed cards lose their sequence entirely.
  showMonth?: boolean;
}): JSX.Element {
  const width = typeof cardWidth === 'number' ? `${cardWidth}px` : cardWidth;
  const { identified, inKit, total } = classifyKitProducts(kit, sectionsByName);
  const pct = total ? identified.length / total : 0;

  const renderRow = (name: string, state: RowState): JSX.Element => {
    const sections = sectionsByName.get(normalizeSupplementName(name));
    return (
      <div key={`${state}-${name}`} style={productRowStyle(kit, state)}>
        {/* No textDecoration here at all — NOT 'none'. Bootstrap's reboot
            already leaves an <a> unadorned and underlines it on hover; setting
            it inline would win over that hover rule and kill the only
            affordance a quiet link has. */}
        <a
          href={productUrl(name, furtherConsiderations)}
          target="_blank"
          rel="noopener noreferrer"
          title={`Learn more about ${name} at Systemic Formulas`}
          style={{
            color: '#212529',
            fontWeight: state === 'identified' ? 'bold' : 500,
            display: 'flex',
            alignItems: 'baseline',
            gap: '5px',
          }}
        >
          {/* The tick looks redundant next to the tint and it isn't: the tint
              is the only OTHER signal, and it all but disappears in grayscale
              (how many clients read the PDF), under red-green color blindness,
              and on a dim screen. One character keeps the state readable in all
              three.
              Deliberately NOT the kit color. Its whole job is to be the signal
              that survives when hue does not, and at 11px on a pale tint three
              of the five brand colors measure under 4.5:1 anyway. */}
          {state === 'identified' && (
            <span
              aria-hidden="true"
              style={{ color: '#212529', fontWeight: 'bold', fontSize: '11px' }}
            >
              ✓
            </span>
          )}
          <span>{name}</span>
        </a>
        {/* A dark neutral, not the kit color. This is the smallest text in the
            card sitting on the card's own tint — the single worst place to put
            a hue. In the brand palette it measured 3.7-3.9:1 for three of the
            five kits, under the 4.5:1 floor; #495057 clears 5.7:1 against every
            one of them. The kit's identity is carried by the top border, the
            title, the ring, the meter and the row accent — none of which are
            11px type. */}
        {state === 'identified' && sections && (
          <SectionOrigins
            sections={sections}
            onSectionClick={onSectionClick}
            color="#495057"
            style={{ fontSize: '11px' }}
          />
        )}
        {/* No "also Month N" on a product that ships in two kits. A repeat is
            not a fact the reader has to reconcile: each month arrives as its
            own packet of that month's supplements, so a product in Months 2
            and 5 is simply in both packets. Naming it invited the question it
            was meant to answer. */}
      </div>
    );
  };

  return (
    <div
      style={{
        width,
        border: '1px solid #e6e9ef',
        borderTop: `4px solid ${kit.color}`,
        borderRadius: '8px',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '9px',
        backgroundColor: '#fff',
        boxShadow,
      }}
    >
      <div style={{ borderBottom: '1px solid #eef0f3', paddingBottom: '9px' }}>
        <div
          style={{
            fontWeight: 'bold',
            fontSize: '15px',
            color: kit.color,
            lineHeight: 1.25,
          }}
        >
          {showMonth ? `Month ${kit.month}: ${kit.name}` : kit.name}
        </div>
        <div
          style={{
            fontSize: '11.5px',
            color: '#6c757d',
            lineHeight: 1.3,
            marginTop: 1,
          }}
        >
          {kit.subtitle ? `${kit.subtitle} · ` : ''}
          {kit.productNames.length} products
        </div>
        <div
          style={{
            height: '5px',
            borderRadius: '3px',
            backgroundColor: '#edf0f2',
            marginTop: '8px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${(pct * 100).toFixed(1)}%`,
              borderRadius: '3px',
              backgroundColor: kit.color,
            }}
          />
        </div>
        <div style={{ fontSize: '10px', color: '#6c757d', marginTop: '4px' }}>
          {identified.length} of {total} products identified
        </div>
      </div>

      {identified.length === 0 && (
        <div
          style={{
            fontSize: '11px',
            lineHeight: 1.4,
            color: '#5b666e',
            backgroundColor: '#f4f7f8',
            borderLeft: '3px solid #c3ced4',
            borderRadius: '0 4px 4px 0',
            padding: '6px 8px',
          }}
        >
          No pattern in this test points into this kit. Month {kit.month} still
          runs the {kit.subtitle || kit.name} phase of the protocol.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        {identified.map(name => renderRow(name, 'identified'))}
        {identified.length > 0 && inKit.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '3px 0 1px',
              fontSize: '9px',
              letterSpacing: '0.11em',
              textTransform: 'uppercase',
              color: '#565f66',
            }}
          >
            <span>Also in this kit</span>
            <span style={{ flex: 1, height: '1px', backgroundColor: '#eceff1' }} />
          </div>
        )}
        {inKit.map(name => renderRow(name, 'inKit'))}
      </div>
    </div>
  );
}

// The coverage ring on a month's node. Reads as a shape across the spine: how
// much of this client's physiology lands in each phase of the protocol, at a
// glance and different for every test.
//
// A zero-coverage month gets a DASHED ring rather than nothing — present but
// unfilled, which is the honest rendering of "no signal here" and cannot be
// mistaken for "this month is missing".
//
// The count sits BELOW the ring, never inside it, matching the Results
// Overview's own ring convention.
function CoverageRing({
  pct,
  color,
}: {
  pct: number;
  color: string;
}): JSX.Element {
  const r = 17;
  const circumference = 2 * Math.PI * r;
  const on = pct * circumference;
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
      <circle cx="22" cy="22" r={r} fill="none" stroke="#e3e8eb" strokeWidth="4" />
      {pct > 0 ? (
        <circle
          cx="22"
          cy="22"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={`${on} ${circumference - on}`}
          transform="rotate(-90 22 22)"
        />
      ) : (
        <circle
          cx="22"
          cy="22"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="4"
          strokeOpacity="0.28"
          strokeDasharray="2 4"
        />
      )}
    </svg>
  );
}

// The non-kit recommendations that start in this month, under that month's kit
// card in the same rail column, so a month reads as one unit: what ships, then
// what to add.
//
// Dashed throughout, and carrying NO kit color. On this panel color means
// "part of the roadmap", so tinting this box in the month's hue would blur the
// one line it exists to draw. There is no "not in the kit · separate purchase"
// subtitle either — the box hangs outside the kit card in a dashed frame, which
// says it already.
function AddOnBox({
  addOns,
  furtherConsiderations,
  onSectionClick,
}: {
  addOns: ScheduledAddOn[];
  furtherConsiderations: FurtherConsiderations;
  onSectionClick?: (section: Section) => void;
}): JSX.Element | null {
  if (!addOns.length) return null;
  return (
    <div
      style={{
        marginTop: '10px',
        border: '1px dashed #bfcbd2',
        borderRadius: '8px',
        padding: '10px 12px 11px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <div style={{ borderBottom: '1px dashed #e3e9ec', paddingBottom: '7px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '12.5px', color: '#495057' }}>
          Targeted Additions
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {addOns.map(addOn => {
          const link =
            addOn.link ?? furtherConsiderations.supplementLinkByName(addOn.name);
          return (
            <div
              key={addOn.name}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '3px',
                fontSize: '12px',
              }}
            >
              {link ? (
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Learn more about ${addOn.name}`}
                  style={{
                    color: '#212529',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '5px',
                  }}
                >
                  <span aria-hidden="true" style={{ color: '#565f66', fontWeight: 'bold' }}>
                    +
                  </span>
                  <span>{addOn.name}</span>
                </a>
              ) : (
                <span style={{ fontWeight: 600, color: '#212529' }}>
                  <span aria-hidden="true" style={{ color: '#565f66' }}>
                    +{' '}
                  </span>
                  {addOn.name}
                </span>
              )}
              <SectionOrigins
                sections={addOn.sections}
                onSectionClick={onSectionClick}
                color="#5f6f78"
                style={{ fontSize: '10.5px' }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// The premise of the whole panel, stated once above the rail.
//
// This was a four-key legend with a swatch per row state. The keys are gone
// with the state they were keying: one of the four ("Not read by blood work")
// described a row that no longer exists, and the other three were labelling
// what the card already says in words — a tick and a tint on the rows a pattern
// points to, an "Also in this kit" rule above the rest, and a dashed box
// captioned "Targeted Additions".
//
// The sentence stays. It is not a key, it is the thing a practitioner has to
// know before the highlighting means anything: a highlighted product is "this
// test points here", never "the others are unnecessary".
function RoadmapPremise(): JSX.Element {
  return (
    <div
      style={{
        fontSize: '12px',
        color: '#6c757d',
        lineHeight: 1.45,
        marginBottom: '14px',
      }}
    >
      Every kit is bought whole. <strong>All</strong> of its products ship
      regardless of what this test flags. A targeted addition is a separate
      purchase, scheduled into the month it starts.
    </div>
  );
}

// The section-level summary, above the rail.
//
// One sentence that does three jobs at once: it gives the practitioner a
// prioritization, it frames a sparse result POSITIVELY instead of leaving a
// blank, and it is the line that makes a Systemic Formulas practitioner see
// what the software is for. The "runs all five regardless" clause only appears
// when it needs to — when at least one month came back empty.
export function roadmapCoverage(
  sectionsByName: Map<string, Section[]>,
  addOnCount: number,
): { monthsHit: number; identified: number; total: number; addOnCount: number } {
  let identified = 0;
  let total = 0;
  let monthsHit = 0;
  HOLOBIOME_KITS.forEach(kit => {
    const c = classifyKitProducts(kit, sectionsByName);
    identified += c.identified.length;
    total += c.total;
    if (c.identified.length > 0) monthsHit += 1;
  });
  return { monthsHit, identified, total, addOnCount };
}

// The coverage sentence, and the only place the words "Holobiome Roadmap"
// appear on screen now that the section is titled for the brand rather than for
// one of its three views.
//
// It reads as a labelled statement rather than a sentence with a name buried in
// it: the bold lead-in says WHICH of the three views this is, which the section
// header no longer does. The label is off in print, where the card header
// already carries it and a second copy an inch below would be the same words
// twice.
function RoadmapHeadline({
  sectionsByName,
  addOnCount,
  withLabel = true,
}: {
  sectionsByName: Map<string, Section[]>;
  addOnCount: number;
  withLabel?: boolean;
}): JSX.Element {
  const { monthsHit, identified, total } = roadmapCoverage(
    sectionsByName,
    addOnCount,
  );
  return (
    <div
      style={{
        fontSize: '13.5px',
        lineHeight: 1.45,
        color: '#212529',
        marginBottom: '4px',
      }}
    >
      {withLabel && (
        <>
          <strong>Holobiome Roadmap</strong>{' '}
        </>
      )}
      Patterns in this test point into{' '}
      <strong>
        {monthsHit === HOLOBIOME_KITS.length
          ? 'all five'
          : `${monthsHit} of the ${HOLOBIOME_KITS.length}`}
      </strong>{' '}
      Holobiome months, covering <strong>{identified} of the {total}</strong>{' '}
      kit products.
      {addOnCount > 0 && (
        <>
          {' '}
          Another <strong>{addOnCount}</strong> product
          {addOnCount === 1 ? '' : 's'} sit outside the kits, scheduled under the
          month {addOnCount === 1 ? 'it starts' : 'they start'}.
        </>
      )}
      {monthsHit < HOLOBIOME_KITS.length && (
        <span style={{ color: '#6c757d' }}>
          {' '}
          The roadmap runs all five regardless. Each month builds the ground the
          next one works on.
        </span>
      )}
    </div>
  );
}

// The ask and the incentive, stacked — but they are two different offers and
// they no longer travel together.
//
// THE BUTTON sells the five-month PROGRAM, so it belongs to the view that shows
// the program. Under the shortlist or the by-system list it was asking the
// practitioner to buy a protocol neither of those views is describing.
//
// THE DISCOUNT applies to any Systemic Formulas order, whichever way the
// products are laid out, so it follows all three views. It is the one thing in
// this strip a practitioner needs whatever they are looking at.
//
// Both are appended in code the way the LABSMARTS10 bullet already is — never
// typed into a sheet.
const HOLOBIOME_ROADMAP_URL = 'https://systemicformulas.com/holobiome-roadmap';

function RoadmapButton(): JSX.Element {
  return (
    <a
        href={HOLOBIOME_ROADMAP_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '7px',
          backgroundColor: HOLOBIOME_ANCHOR,
          color: '#fff',
          fontSize: '13px',
          fontWeight: 600,
          padding: '8px 15px',
          borderRadius: '5px',
          whiteSpace: 'nowrap',
        }}
      >
      Explore the Holobiome Roadmap →
    </a>
  );
}

function DiscountCode(): JSX.Element {
  return (
    <div style={{ fontSize: '11.5px', color: '#6c757d', whiteSpace: 'nowrap' }}>
        Use code{' '}
        {/* The dark stop of the app's own header gradient (index.css), not the
            button blue: on this pale chip #337ab7 measures 3.95:1, under the
            floor, and this is the one string a practitioner has to read
            character by character. */}
        <span
          style={{
            fontWeight: 'bold',
            letterSpacing: '0.04em',
            color: '#265a88',
            backgroundColor: '#eef2fb',
            border: '1px dashed #b7c6e4',
            borderRadius: '3px',
            padding: '1px 5px',
          }}
        >
          LABSMARTS10
        </span>{' '}
      for 10% off your first order
    </div>
  );
}

// Right-hand column of the closing strip: the discount always, the button above
// it only where the program is on screen. A column even when it holds one item,
// so the discount sits in the same place either way rather than jumping to
// where the button used to be.
//
// `marginLeft: auto` rather than relying on the strip's space-between. This
// column is the strip's ONLY child, and space-between puts a lone child on the
// left — which is where the discount was landing. The auto margin eats whatever
// space is free, so the column is hard against the right edge. `alignItems:
// flex-end` then right-aligns the button to the discount under it, so the two
// share a right edge.
function RoadmapCta({ withButton }: { withButton: boolean }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '6px',
        marginLeft: 'auto',
      }}
    >
      {withButton && <RoadmapButton />}
      <DiscountCode />
    </div>
  );
}

// The section's closing strip: the ask.
//
// IT IS SCREEN-ONLY, so this strip prints nothing at all. The general rule on
// this panel is that anything on screen prints, but the rule is really
// "anything CLIENT-facing prints", and the roadmap button and the LABSMARTS10
// code are neither: the discount is the practitioner's trade account, and the
// exported report is the document that goes to the client. Printing it would
// put a wholesale incentive in front of the person being sold to.
//
// It used to carry one more thing — a note explaining that two products were
// never counted in a month's total — which went with the state that produced
// the number it was explaining.
//
// The discount reaches the PDF through no other route: the "Use code …" bullet
// on every Supplements row is dropped by parseSupplementItem, which returns
// null for it, so the printed product tables never carry it either.
function RoadmapFooter({
  withRoadmapButton = true,
}: {
  // The button alone. It sells the five-month program, so it belongs to the
  // view that shows the program; the discount under it follows every view.
  withRoadmapButton?: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        marginTop: '16px',
        paddingTop: '12px',
        borderTop: '1px solid #e9ecef',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <RoadmapCta withButton={withRoadmapButton} />
      </div>
    </div>
  );
}

// The month spine.
//
// The five kits used to sit in a wrapping flex row, which read as five
// unrelated categories and — worse — re-wrapped to 3+2 or 4+1 at intermediate
// widths, destroying the sequence the word "roadmap" promises. A rail with a
// node per month fixes the reading order in the layout itself.
//
// It WRAPS rather than scrolling. It used to scroll sideways — the reasoning
// being that wrapping is what destroyed the sequence in the first place — but
// that traded a visible problem for an invisible one: at anything under the
// rail's natural width Month 5 simply sat off the right edge, and a month the
// reader never sees is strictly worse than a month on the next line. The month
// number, the ring and the kit name are on every column, so the sequence
// survives a wrap; being cut off, it does not.
//
// A grid rather than a flex row, because `auto-fit` + `minmax` keeps every
// column the same width including the last row's. Flex would stretch a lone
// wrapped card to the full width of the panel.
const RAIL_MIN_COLUMN = 200;
// Vertical center of a node, so the connectors meet the ring's midline.
const RAIL_NODE_CENTER = 25;

function MonthRail({
  sectionsByName,
  addOnsByMonth,
  furtherConsiderations,
  onSectionClick,
}: {
  sectionsByName: Map<string, Section[]>;
  addOnsByMonth: Map<number, ScheduledAddOn[]>;
  furtherConsiderations: FurtherConsiderations;
  onSectionClick?: (section: Section) => void;
}): JSX.Element {
  const railRef = React.useRef<HTMLDivElement>(null);

  // Hide the connector stubs that would point at empty space.
  //
  // Once the grid wraps, the first column of a row has nothing to its left and
  // the last has nothing to its right, so those halves have to go — otherwise
  // each row ends in a line running off into white. Only the browser knows
  // where the rows broke, so this is measured after layout (offsetTop groups
  // columns into rows) and re-measured whenever the rail resizes.
  React.useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    const sync = (): void => {
      const columns = Array.from(
        rail.querySelectorAll<HTMLElement>('[data-rail-column]'),
      );
      columns.forEach((column, i) => {
        const sameRow = (other?: HTMLElement): boolean =>
          !!other && other.offsetTop === column.offsetTop;
        const half = (side: string): HTMLElement | null =>
          column.querySelector<HTMLElement>(`[data-rail-connector="${side}"]`);
        const left = half('left');
        const right = half('right');
        // visibility, not display: the halves are absolutely positioned, so
        // hiding them must not affect the column's own layout and retrigger
        // the observer.
        if (left) left.style.visibility = sameRow(columns[i - 1]) ? 'visible' : 'hidden';
        if (right) right.style.visibility = sameRow(columns[i + 1]) ? 'visible' : 'hidden';
      });
    };
    sync();
    // Read off window rather than the global: this project's TS lib predates
    // ResizeObserver, and jsdom does not implement it either — so the tests
    // exercise the initial sync and fall back to the resize event.
    const Observer = (window as any).ResizeObserver;
    if (Observer) {
      const observer = new Observer(sync);
      observer.observe(rail);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  });

  // Half a segment each, both offset from the column's LEFT edge: the left half
  // starts at 0, the right half at the midpoint. Because the columns touch
  // (they carry padding rather than a grid gap), the halves meet and the nodes
  // on one row read as a continuous spine.
  const connector = (side: 'left' | 'right'): React.CSSProperties => ({
    position: 'absolute',
    top: `${RAIL_NODE_CENTER}px`,
    left: side === 'left' ? 0 : '50%',
    width: '50%',
    height: '2px',
    backgroundColor: '#dbe2e6',
  });
  return (
    <div style={{ overflowX: 'auto', paddingBottom: '4px' }}>
      <div ref={railRef}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(${RAIL_MIN_COLUMN}px, 1fr))`,
            alignItems: 'start',
            rowGap: '26px',
          }}
        >
          {HOLOBIOME_KITS.map((kit, index) => {
            const { identified, total } = classifyKitProducts(
              kit,
              sectionsByName,
            );
            const pct = total ? identified.length / total : 0;
            return (
              <div
                key={kit.key}
                data-rail-column={kit.month}
                style={{ padding: '0 8px', position: 'relative' }}
              >
                {index > 0 && (
                  <div data-rail-connector="left" style={connector('left')} />
                )}
                {index < HOLOBIOME_KITS.length - 1 && (
                  <div data-rail-connector="right" style={connector('right')} />
                )}
                <div
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    width: '56px',
                    height: '52px',
                    margin: '0 auto 4px',
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CoverageRing pct={pct} color={kit.color} />
                </div>
                <div
                  style={{
                    textAlign: 'center',
                    marginBottom: '12px',
                    lineHeight: 1.25,
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      fontSize: '9.5px',
                      letterSpacing: '0.13em',
                      textTransform: 'uppercase',
                      color: '#565f66',
                    }}
                  >
                    Month {kit.month}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      // A zero-coverage month drops to a neutral rather than
                      // the kit hue, so the ring and the count agree. Still
                      // dark enough to read: this is a number, not decoration.
                      color: identified.length ? kit.color : '#565f66',
                    }}
                  >
                    {identified.length}/{total}
                  </span>
                </div>
                <KitCard
                  kit={kit}
                  sectionsByName={sectionsByName}
                  furtherConsiderations={furtherConsiderations}
                  onSectionClick={onSectionClick}
                />
                <AddOnBox
                  addOns={addOnsByMonth.get(kit.month) ?? []}
                  furtherConsiderations={furtherConsiderations}
                  onSectionClick={onSectionClick}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Top Priority, By System, or Roadmap. Three audiences, three views — the
// practitioner who wants to know what to start today, the one who wants the
// specific products by system, and the one buying the program — rather than
// stacked tables of the same list with a checkbox to hide the duplicates.
//
// They narrow left to right, and TOP PRIORITY LEADS because it is the view the
// panel opens on: a whole report's worth of recommendations is not an answer
// to "what first", and the shortlist is. By System is the same list unabridged,
// and the roadmap is those products laid on the program's calendar. Every
// product in a view to the left also appears in the ones to its right, with the
// same start month — the three are one collection at three degrees of detail,
// not three opinions.
//
// It was the Systemic Formulas blue, which sat inches under the card's own
// #337ab7 header — those two measure 1.06:1 against each other, near-identical
// lightness at slightly different hues, which is the worst place two colors
// can be: too close to read as deliberate, too far to read as the same. It is
// a neutral now; SegmentedToggle carries the rest of that reasoning, and both
// of this panel's switches share it.
export type RoadmapView = 'top' | 'roadmap' | 'system';

// The name of whichever list view is open, WITH ITS FIRST SENTENCE ON THE SAME
// LINE: "Top Priority Supplements — The 7 products to start with, drawn from
// the By System list." The rest of the explanation follows as its own
// paragraph.
//
// A bare heading over a paragraph made the reader take two steps to learn one
// thing; a heading that says what the view is in the same breath takes one. The
// title stays bold and near-black and the sentence takes the paragraph's gray,
// so the line still reads as a heading with something appended rather than as a
// long title.
//
// The negative top margin is optical alignment, not spacing. The view switch
// beside this is a bordered pill, so its visible top edge is its BOX; a line of
// text's visible top edge is its cap height, which sits a few px below the box
// it's in. Top-aligning the two containers therefore left the heading looking
// low. Four pixels up puts the cap line level with the switch's top edge, which
// is what "aligned" means to the eye.
function ViewHeading({
  title,
  lead,
}: {
  title: string;
  // A node, not a string: the Top Priority lead bolds "By System" in it, which
  // is the name of the view it is pointing at.
  lead: React.ReactNode;
}): JSX.Element {
  return (
    // The whole line in the report's near-black, the lead included. It was a
    // bold title on a gray sentence, which read as a heading with a caption
    // stuck to it; one color reads as one sentence, and the bold still tells
    // the two halves apart.
    <div
      style={{
        fontSize: '14px',
        color: '#212529',
        lineHeight: 1.35,
        marginTop: '-4px',
        marginBottom: '3px',
      }}
    >
      <span style={{ fontWeight: 'bold' }}>{title}</span>
      {' — '}
      {lead}
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: RoadmapView;
  onChange: (view: RoadmapView) => void;
}): JSX.Element {
  return (
    <SegmentedToggle<RoadmapView>
      ariaLabel="Roadmap view"
      value={view}
      options={[
        { value: 'top', label: 'Top Priority' },
        { value: 'system', label: 'By System' },
        { value: 'roadmap', label: 'Roadmap' },
      ]}
      onChange={onChange}
    />
  );
}

// The Holobiome Roadmap body — everything Systemic Formulas in one place.
//
// TOP PRIORITY: the shortlist — each system's top two, ranked by how many
// patterns across the report point at them. Opens here.
//
// ROADMAP: the five kits on a month spine, each with a coverage ring, its
// products led by the ones this test points to, and the non-kit products
// scheduled for that month underneath.
//
// BY SYSTEM: HolobiomeProductTable — every recommended product, kit and
// addition alike, grouped by the system that called for it, with the pattern
// behind it and the month it starts.
//
// Both read their add-on months from ONE computation (collectScheduledAddOns),
// so the month a product shows on the rail and the month it shows in the table
// cannot disagree.
export const SupplementProtocolsPanel: React.FC<Props> = ({
  bloodworkAnalysis,
  furtherConsiderations,
  enabledSupplementBrands,
  onSectionClick,
  clientList,
}) => {
  // View state only — not saved with the test or the profile, so it resets to
  // Top Priority when the report is reopened. Nothing downstream reads it: the
  // PDF prints all three halves regardless, which is what stops the printed
  // report disagreeing with whichever view happened to be open.
  const [view, setView] = React.useState<RoadmapView>('top');
  // The by-system view's listing choice. Owned here rather than by the table,
  // because the control that flips it now sits under the view switch with the
  // rest of this section's chrome. Defaults to listing everywhere: the
  // practitioner reading a system's list wants that system's whole
  // recommendation, and "once each" is the reference view, for counting bottles
  // rather than reading a plan. View state only, so it resets when the report
  // is reopened, and the PDF prints the default.
  const [listEverywhere, setListEverywhere] = React.useState(true);

  // Below the hook, not above it: the early returns must not change how many
  // hooks this component calls.
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return null;
  // Same brand gate as hasSupplementProtocolsContent, so a caller that renders
  // the panel without checking first still respects the Systemic Formulas
  // checkbox.
  if (!isSystemicFormulasEnabled(enabledSupplementBrands)) return null;

  const recommended = collectRecommendedProducts(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );
  const sectionsByName = new Map<string, Section[]>();
  recommended.forEach((product, key) => sectionsByName.set(key, product.sections));
  const addOnsByMonth = collectScheduledAddOns(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );
  let addOnCount = 0;
  addOnsByMonth.forEach(list => {
    addOnCount += list.length;
  });
  const topPriorityCount = collectTopPriorities(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  ).length;
  // Whether each list view has anything to show. The headings moved OUT of the
  // tables and into this row, so they no longer disappear with the table that
  // used to carry them: a test with no Systemic Formulas recommendations was
  // announcing "Top Priority Supplements — The 0 products to start with" over
  // an empty body. hasSupplementProtocolsContent can't gate this, because the
  // Roadmap view has content either way — the five kits always print.
  const hasBySystemContent =
    scopedGroups(
      bloodworkAnalysis,
      furtherConsiderations,
      enabledSupplementBrands,
      'systemicFormulas',
    ).length > 0;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          marginBottom: '10px',
        }}
      >
        {/* EACH VIEW INTRODUCES ITSELF HERE, beside the switch — not under a
            divider halfway down the panel, which is where all three headings
            used to sit. The row was empty white space to the left of the
            switch while the content that belonged in it announced itself
            again below. `minWidth: 0` lets the text wrap inside the column
            instead of forcing the row wider: the paragraph stops where the
            switch begins. */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {view === 'top' && topPriorityCount > 0 && (
            <>
              {/* Not bare "Top Priority", which is the switch's own label two
                  inches to the right: the same string twice reads as a control
                  and its echo. The longer form is parallel with the by-system
                  heading, so the views name themselves the same way. */}
              <ViewHeading
                title="Top Priority Supplements"
                lead={topPriorityLead(topPriorityCount)}
              />
              <TopPriorityIntro />
            </>
          )}
          {view === 'system' && hasBySystemContent && (
            <>
              <ViewHeading
                title="Pattern-Identified Supplements"
                lead={PRODUCT_TABLE_LEAD}
              />
              <ProductTableIntro listEverywhere={listEverywhere} />
            </>
          )}
          {/* The coverage sentence counts MONTHS — "into 4 of the 5 Holobiome
              months" — which is a fact about the program, and the program is
              what the Roadmap view shows. Above the shortlist it was measuring
              something that view doesn't put on screen. It carries its own bold
              label, so it needs no ViewHeading over it. */}
          {view === 'roadmap' && (
            <RoadmapHeadline
              sectionsByName={sectionsByName}
              addOnCount={addOnCount}
            />
          )}
        </div>
        {/* Both controls in one right-hand column. The listing choice used to
            sit over the by-system table, which put two controls for the same
            section in two different places; stacked under the switch they read
            as what they are, the chrome for whichever view is open. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '8px',
            flex: '0 0 auto',
          }}
        >
          <ViewToggle view={view} onChange={setView} />
          {view === 'system' && (
            <ListingChoice
              label="Listing"
              value={listEverywhere ? 'every' : 'once'}
              options={[
                { value: 'every', label: 'In every system' },
                { value: 'once', label: 'Once each' },
              ]}
              onChange={value => setListEverywhere(value === 'every')}
            />
          )}
        </div>
      </div>

      {view === 'roadmap' && (
        <>
          <RoadmapPremise />
          <MonthRail
            sectionsByName={sectionsByName}
            addOnsByMonth={addOnsByMonth}
            furtherConsiderations={furtherConsiderations}
            onSectionClick={onSectionClick}
          />
        </>
      )}
      {view === 'top' && (
        <HolobiomeTopPriorityTable
          bloodworkAnalysis={bloodworkAnalysis}
          furtherConsiderations={furtherConsiderations}
          enabledSupplementBrands={enabledSupplementBrands}
          clientList={clientList}
          onSectionClick={onSectionClick}
        />
      )}
      {view === 'system' && (
        <HolobiomeProductTable
          bloodworkAnalysis={bloodworkAnalysis}
          furtherConsiderations={furtherConsiderations}
          enabledSupplementBrands={enabledSupplementBrands}
          clientList={clientList}
          listEverywhere={listEverywhere}
          onSectionClick={onSectionClick}
        />
      )}

      {/* The roadmap button belongs to the program; the discount belongs to
          every view. */}
      <RoadmapFooter withRoadmapButton={view === 'roadmap'} />
    </>
  );
};

// ───────────────────────── PDF ─────────────────────────
//
// The kit boxes and the Add-On Products table, rendered for the Analysis PDF.
// The third block of the on-screen section — Pattern-Identified Supplements —
// is SupplementsSummaryPanel in its 'systemicFormulas' scope, which paginates
// itself; the PDF container renders it right after this one, so the printed
// roadmap reads in the same order as the screen.
//
// Two things differ from the screen. The cards are narrower (three across a
// portrait page instead of a wrapping row of five), and the rows of cards and
// the table rows are packed into explicit pages here rather than left to
// html2pdf — see pdfHolobiomePagination.ts for why this PDF never trusts
// page-break-inside.

// The shortlist prints as its own block, ahead of the kits, for the reason it
// opens the panel on screen: the client reading the printed report meets "start
// with these" before the five months that spell out everything else. Its own
// block and not a page of the kits' one, because it carries its own "Include in
// PDF" box — a practitioner sending only the shortlist unticks the other two.
//
// PAGINATED, and it has to be. The obvious reading is that a shortlist capped
// at seven rows cannot overflow a page, and that was the original claim here —
// but the cap stretches to TOP_PRIORITY_MAX for a Priority-heavy test, and the
// legend above the table is unbounded: it names every pattern the shortlist
// cites, ACROSS SYSTEMS, which on the report that prompted this ran to fourteen
// entries for seven products. Ten rows of four-line descriptions under a
// twenty-entry legend clears 900px, and html2pdf would slice straight through
// the table — the exact failure the whole pre-pagination scheme exists to stop
// (see pdfHolobiomePagination).
//
// So the rows are packed like every other long block here. The legend is
// charged to the first page as an item of its own, and the continuation pages
// number their rows against the WHOLE shortlist's legend rather than their own
// slice of it — see TopPriorityTable's startRank and legendPatterns.
const PDF_TOP_INTRO_HEIGHT = 58;
// One legend row holds two patterns.
const PDF_TOP_LEGEND_ROW = 20;
const PDF_TOP_LEGEND_BASE = 22;
// A product row: name, a description that runs to four lines at print width,
// and a two-line Why cell.
const PDF_TOP_ROW_HEIGHT = 62;
const PDF_TOP_TABLE_HEADER = 24;
export const HolobiomeTopPriorityPdf: React.FC<PdfProps> = ({
  bloodworkAnalysis,
  furtherConsiderations,
  enabledSupplementBrands,
}) => {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return null;
  if (!isSystemicFormulasEnabled(enabledSupplementBrands)) return null;
  const priorities = collectTopPriorities(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );
  if (priorities.length === 0) return null;

  // The whole shortlist's legend, built once so every page numbers against it.
  const { patterns } = topPriorityLegend(priorities);
  const firstPageExtra =
    PDF_TOP_INTRO_HEIGHT +
    PDF_TOP_TABLE_HEADER +
    (patterns.length > 0
      ? PDF_TOP_LEGEND_BASE +
        Math.ceil(patterns.length / 2) * PDF_TOP_LEGEND_ROW
      : 0);
  const pages = packHolobiomeItems<TopPriority>({
    items: priorities,
    heights: priorities.map(() => PDF_TOP_ROW_HEIGHT),
    pageCapacity: PDF_PAGE_CAPACITY,
    cardOverhead: PDF_CARD_OVERHEAD,
    firstPageExtra,
    itemGap: 0,
  });
  const unrepresented = unrepresentedSystemCount(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );

  // The container emits the break and spacer that open this block, so the FIRST
  // card doesn't force one of its own; a continuation page does.
  let rank = 1;
  return (
    <>
      {pages.map((page, i) => {
        const startRank = rank;
        rank += page.items.length;
        return (
          <PdfCard
            key={`holobiome-top-${i}`}
            title={PDF_TOP_PRIORITY_TITLE}
            forceBreak={i > 0}
            continued={i > 0}
          >
            {i === 0 && (
              <>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#6c757d',
                    lineHeight: 1.2,
                    marginBottom: '3px',
                  }}
                >
                  {topPriorityLead(priorities.length)}
                </div>
                <TopPriorityIntro />
              </>
            )}
            {/* No client-list column: a checkbox on paper is a decision nobody
                can make. The legend prints once, on the first page; later pages
                still cite its numbers, which is why they are given it rather
                than building their own. */}
            <TopPriorityTable
              priorities={page.items}
              legendPatterns={patterns}
              showLegend={i === 0}
              startRank={startRank}
              withProductHint={false}
            />
            {i === pages.length - 1 && (
              <UnrepresentedNote count={unrepresented} />
            )}
          </PdfCard>
        );
      })}
    </>
  );
};


// Does this test have a shortlist to print? The container needs this before it
// emits the break and spacer that open the block, or an empty shortlist costs a
// forced page break plus a spacer above whatever follows.
export function hasHolobiomeTopPriorityPdfContent(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  enabledSupplementBrands: string[] | undefined,
): boolean {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return false;
  if (!isSystemicFormulasEnabled(enabledSupplementBrands)) return false;
  return (
    collectTopPriorities(
      bloodworkAnalysis,
      furtherConsiderations,
      enabledSupplementBrands,
    ).length > 0
  );
}

// One card title — the roadmap now prints as a single block of kit columns
// rather than kits followed by a separate add-on table, so continuation pages
// say "(cont.)" off this one title.
// Carries the byline, so the printed report and the screen header read the
// same. The screen copy lives in bloodwork-test-analysis.tsx.
// The three printed blocks are named for the SECTION, then for which of its
// three views they are — the same way round the section header and the view
// switch now read. The kits block is the one that is the Holobiome Roadmap; the
// other two are lists of products, and were never the roadmap.
const PDF_KITS_TITLE = 'Systemic Formulas — Holobiome Roadmap';
// Passed to SupplementsSummaryPanel by the PDF container for the by-system half.
export const PDF_PATTERN_TABLE_TITLE =
  'Systemic Formulas — Pattern-Identified Supplements';
// The shortlist prints FIRST, ahead of the kits, for the reason it opens the
// panel on screen: the client reading the printed report meets "start with
// these" before the five months that spell out everything else.
//
// One page, always. The block is capped at TOP_PRIORITY_COUNT rows, and even at
// the pessimistic end — card overhead (76) + the intro and legend (~150) + a
// header row + seven four-line description rows (~60 each) — it lands near
// 670px against PDF_PAGE_CAPACITY's 900, so it needs none of the packing the
// kit rows and the by-system table do.
const PDF_TOP_PRIORITY_TITLE =
  'Systemic Formulas — Top Priority Supplements';

// Space above a PDF card that opens a fresh page, on top of the 10px top margin
// every .card carries (index.css) — so a card header sits 16px below the page
// edge, the same as every section title. The container uses this for the blocks
// it opens itself (the roadmap, the Supplements panel); PdfCard uses it for its
// own continuation pages. A big buffer was protection against html2canvas's
// page-grid drift, which is now zero — see PDF_CAPTURE_WIDTH_PX in
// bloodwork-analysis-pdf.tsx.
export const PDF_CARD_TOP_SPACER = 6;

// Layout: three columns per row, so the five months print 3 + 2. Each column is
// a third of the card body less its share of the gaps — percentages rather than
// a pixel width because the body is narrower than it looks: the 801px page
// loses 40px to .pdf-page's side padding, 2px to the card border and 30px to
// .card-body's padding, leaving ~729px. That works out to ~234px a column. A
// fixed 248px was what printed Month 3 clipped at the right edge (3 x 248 + 2
// gaps = 772 against 729). Five across (the screen rail) would be 1339px, which
// is why print does not get the spine — a rail split across a page break says
// nothing, and the month is back on the card title instead.
const PDF_KIT_GAP = 14;
const PDF_KITS_PER_ROW = 3;
const PDF_KIT_CARD_WIDTH = `calc(${(100 / PDF_KITS_PER_ROW).toFixed(4)}% - ${(
  (PDF_KIT_GAP * (PDF_KITS_PER_ROW - 1)) /
  PDF_KITS_PER_ROW
).toFixed(2)}px)`;
// The card's drop shadow is a screen affordance and html2canvas renders it
// wrong: instead of a soft outer shadow it paints a hard rgba(0,0,0,0.06) band
// several px wide INSIDE the card's right padding — the grey stripe that showed
// up beside the products in the right-hand kits. Print gets no shadow.
const PDF_KIT_CARD_SHADOW = 'none';

// Height estimates in html2pdf canvas px. Same budget the Supplements panel
// uses (its PDF_PAGE_CAPACITY / PDF_CARD_OVERHEAD), so the two roadmap halves
// break on the same grid. Erring high only costs whitespace; erring low puts
// content in the footer.
const PDF_PAGE_CAPACITY = 900;
const PDF_CARD_OVERHEAD = 76;
// The headline and the premise line, on the first page only. Left at 92 now
// that the legend's key row is gone: erring high costs whitespace, and this is
// the one estimate a shrunken block cannot break.
const PDF_INTRO_HEIGHT = 92;
// Card padding + borders + title + subtitle + the coverage meter and its
// caption + the header divider.
const PDF_KIT_CARD_BASE = 124;
// One product row and the gap under it...
const PDF_KIT_PRODUCT_ROW = 35;
// ...plus the second line an identified row carries: its section origins. An
// inKit row has none, so it is a single line.
const PDF_KIT_SECOND_LINE = 20;
// The "Also in this kit" rule between the identified rows and the rest.
const PDF_KIT_SPLIT_ROW = 20;
// The note a kit with no identified product carries instead of a blank body.
const PDF_KIT_ZERO_NOTE = 46;
// Targeted Additions box: frame, title and divider, then a row per product with
// its origins line under it.
const PDF_ADD_ON_BOX_BASE = 58;
const PDF_ADD_ON_ROW = 34;
// There is no closing strip in print. The roadmap button and discount code are
// the practitioner's, not the client's, and the note that used to sit beside
// them went with the row state it explained. The dosing block is not here
// either — the kits are premade, so dosing belongs to the pattern-identified
// products and prints with them (see DosingNote).

const estimateKitCardHeight = (
  kit: HolobiomeKit,
  sectionsByName: Map<string, Section[]>,
): number => {
  const { identified, inKit } = classifyKitProducts(kit, sectionsByName);
  let height = PDF_KIT_CARD_BASE;
  if (identified.length === 0) height += PDF_KIT_ZERO_NOTE;
  if (identified.length > 0 && inKit.length > 0) height += PDF_KIT_SPLIT_ROW;
  identified.forEach(() => {
    height += PDF_KIT_PRODUCT_ROW + PDF_KIT_SECOND_LINE;
  });
  height += inKit.length * PDF_KIT_PRODUCT_ROW;
  return height;
};

const estimateAddOnBoxHeight = (addOns: ScheduledAddOn[]): number =>
  addOns.length === 0
    ? 0
    : PDF_ADD_ON_BOX_BASE + addOns.length * PDF_ADD_ON_ROW;

// A printed column is a kit card plus the additions scheduled for its month —
// they travel together, because splitting a month's box from its kit is the one
// break that would misread ("whose additions are these?").
type PdfKitColumn = { kit: HolobiomeKit; addOns: ScheduledAddOn[] };

// Chunk the five columns into rows of three. A row is the atom the packer
// moves, so columns never end up split across a page boundary from their
// rowmates.
function kitRows(columns: PdfKitColumn[]): PdfKitColumn[][] {
  const rows: PdfKitColumn[][] = [];
  for (let i = 0; i < columns.length; i += PDF_KITS_PER_ROW) {
    rows.push(columns.slice(i, i + PDF_KITS_PER_ROW));
  }
  return rows;
}

// Everything the PDF renderer and the container's height estimate both need,
// computed once from the same inputs so the estimate can never describe a
// different layout than the one that prints.
function buildRoadmapPdfPages(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  enabledSupplementBrands: string[] | undefined,
) {
  const recommended = collectRecommendedProducts(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );
  const sectionsByName = new Map<string, Section[]>();
  recommended.forEach((product, key) =>
    sectionsByName.set(key, product.sections),
  );
  const addOnsByMonth = collectScheduledAddOns(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );
  let addOnCount = 0;
  addOnsByMonth.forEach(list => {
    addOnCount += list.length;
  });

  const columns: PdfKitColumn[] = HOLOBIOME_KITS.map(kit => ({
    kit,
    addOns: addOnsByMonth.get(kit.month) ?? [],
  }));
  const rows = kitRows(columns);
  const kitPages = packHolobiomeItems<PdfKitColumn[]>({
    items: rows,
    heights: rows.map(row =>
      Math.max(
        ...row.map(
          column =>
            estimateKitCardHeight(column.kit, sectionsByName) +
            estimateAddOnBoxHeight(column.addOns),
        ),
      ),
    ),
    pageCapacity: PDF_PAGE_CAPACITY,
    cardOverhead: PDF_CARD_OVERHEAD,
    firstPageExtra: PDF_INTRO_HEIGHT,
    itemGap: PDF_KIT_GAP,
  });

  return { sectionsByName, addOnCount, kitPages };
}

// How tall the roadmap's LAST printed page is, for the container's
// disclaimer-placement math. 0 when the section isn't in the PDF at all.
export function estimatedHolobiomeRoadmapPdfHeight(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  enabledSupplementBrands: string[] | undefined,
): number {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return 0;
  if (!isSystemicFormulasEnabled(enabledSupplementBrands)) return 0;
  const { kitPages } = buildRoadmapPdfPages(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );
  return kitPages.length > 0 ? kitPages[kitPages.length - 1].height : 0;
}

// One PDF card — one printed page.
//
// `forceBreak` and `continued` are deliberately separate. Every card here gets
// a page of its own, so all but the very first force a break; but only a card
// that carries the SPILLOVER of the block above it says "(cont.)".
//
// The break is followed by the same top buffer the container puts before the
// first card: a forced break lands a hair above the true page edge, and without
// the spacer the card header gets clipped.
function PdfCard({
  title,
  forceBreak,
  continued,
  children,
}: {
  title: string;
  forceBreak: boolean;
  continued: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <>
      {forceBreak && (
        <>
          <div className="html2pdf__page-break" />
          <div style={{ height: PDF_CARD_TOP_SPACER }} aria-hidden />
        </>
      )}
      <Card className="disclaimer-panel mb-5">
        <Card.Header className="py-1">
          {continued ? `${title} (cont.)` : title}
        </Card.Header>
        <Card.Body
          className="disclaimer-body note-card-body"
          style={{ paddingBottom: 12 }}
        >
          {children}
        </Card.Body>
      </Card>
    </>
  );
}

interface PdfProps {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
}

// The kit columns for the PDF.
//
// The on-screen Roadmap / By System toggle is view state in
// SupplementProtocolsPanel — never saved with the test, and in a different
// window from this one. The PDF therefore prints BOTH halves regardless of
// which view happened to be open, which is what stops the printed report
// disagreeing with the screen. "Include in PDF" on the section header is what
// takes the whole roadmap out.
export const HolobiomeRoadmapPdf: React.FC<PdfProps> = ({
  bloodworkAnalysis,
  furtherConsiderations,
  enabledSupplementBrands,
}) => {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return null;
  if (!isSystemicFormulasEnabled(enabledSupplementBrands)) return null;

  const { sectionsByName, addOnCount, kitPages } = buildRoadmapPdfPages(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );

  // The container already emitted the break and spacer that put the first kit
  // page on a fresh page, so that one card alone doesn't force its own.
  return (
    <>
      {kitPages.map((page, i) => (
        <PdfCard
          key={`holobiome-kits-${i}`}
          title={PDF_KITS_TITLE}
          forceBreak={i > 0}
          continued={i > 0}
        >
          {i === 0 && (
            <>
              {/* No label: this card's own header already says Holobiome
                  Roadmap. */}
              <RoadmapHeadline
                sectionsByName={sectionsByName}
                addOnCount={addOnCount}
                withLabel={false}
              />
              <RoadmapPremise />
            </>
          )}
          {page.items.map(row => (
            <div
              key={row.map(column => column.kit.key).join('-')}
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                gap: `${PDF_KIT_GAP}px`,
                alignItems: 'flex-start',
                marginBottom: `${PDF_KIT_GAP}px`,
              }}
            >
              {row.map(column => (
                <div
                  key={column.kit.key}
                  style={{ flex: `0 0 ${PDF_KIT_CARD_WIDTH}`, width: PDF_KIT_CARD_WIDTH }}
                >
                  <KitCard
                    kit={column.kit}
                    sectionsByName={sectionsByName}
                    furtherConsiderations={furtherConsiderations}
                    boxShadow={PDF_KIT_CARD_SHADOW}
                    showMonth
                  />
                  <AddOnBox
                    addOns={column.addOns}
                    furtherConsiderations={furtherConsiderations}
                  />
                </div>
              ))}
            </div>
          ))}
        </PdfCard>
      ))}
    </>
  );
};
